if (!customElements.get("social-videos")) {
  customElements.define(
    "social-videos",
    class SocialVideos extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        this.track = this.querySelector("[data-social-videos-track]");
        this.cards = Array.from(this.querySelectorAll("[data-social-videos-card]"));
        this.dots = Array.from(this.querySelectorAll("[data-social-videos-dot]"));
        this.videos = Array.from(this.querySelectorAll("video"));
        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.hasCenteredInitialMobileCard = false;
        this.wasMobileLayout = null;

        const { signal } = this.abortController;

        this.videos.forEach((video) => {
          video.addEventListener("play", () => this.pauseOtherVideos(video), { signal });
        });

        if (this.track) {
          this.track.addEventListener("scroll", this.handleScroll, { passive: true, signal });
          window.addEventListener("resize", this.handleResize, { passive: true, signal });

          this.dots.forEach((dot) => {
            dot.addEventListener(
              "click",
              () => {
                const index = Number(dot.getAttribute("data-social-videos-index"));
                this.scrollToCard(index);
              },
              { signal }
            );
          });

          this.handleResize();
        }
      }

      disconnectedCallback() {
        if (this.initialScrollFrame) {
          window.cancelAnimationFrame(this.initialScrollFrame);
          this.initialScrollFrame = null;
        }

        if (this.scrollFrame) {
          window.cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = null;
        }

        if (this.abortController) {
          this.abortController.abort();
        }
      }

      handleScroll() {
        if (this.scrollFrame) {
          return;
        }

        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateActiveDot();
        });
      }

      handleResize() {
        const isMobileLayout = this.isMobileLayout();

        if (isMobileLayout && (this.wasMobileLayout !== true || !this.hasCenteredInitialMobileCard)) {
          this.centerInitialMobileCard();
        }

        if (!isMobileLayout) {
          this.hasCenteredInitialMobileCard = false;
        }

        this.wasMobileLayout = isMobileLayout;
        this.handleScroll();
      }

      centerInitialMobileCard() {
        if (!this.isMobileLayout() || this.cards.length !== 3) {
          return;
        }

        if (this.initialScrollFrame) {
          window.cancelAnimationFrame(this.initialScrollFrame);
        }

        this.initialScrollFrame = window.requestAnimationFrame(() => {
          this.initialScrollFrame = null;
          this.scrollToCard(1, { behavior: "auto", align: "center" });
          this.hasCenteredInitialMobileCard = true;
          this.updateActiveDot();
        });
      }

      updateActiveDot() {
        if (!this.track || !this.cards.length || !this.dots.length) {
          return;
        }

        const trackRect = this.track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        let activeIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.cards.forEach((card, index) => {
          const rect = card.getBoundingClientRect();
          const cardCenter = rect.left + rect.width / 2;
          const distance = Math.abs(cardCenter - trackCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            activeIndex = index;
          }
        });

        this.dots.forEach((dot, index) => {
          const isActive = index === activeIndex;
          dot.classList.toggle("is-active", isActive);

          if (isActive) {
            dot.setAttribute("aria-current", "true");
          } else {
            dot.removeAttribute("aria-current");
          }
        });
      }

      scrollToCard(index, options = {}) {
        const target = this.cards[index];

        if (!this.track || !target) {
          return;
        }

        const { behavior = "smooth", align = this.isMobileLayout() ? "center" : "start" } = options;
        const trackStyles = window.getComputedStyle(this.track);
        const paddingLeft = Number.parseFloat(trackStyles.paddingLeft) || 0;
        let left = target.offsetLeft - paddingLeft;

        if (align === "center") {
          left = target.offsetLeft - (this.track.clientWidth - target.offsetWidth) / 2;
        }

        const maxLeft = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);

        this.track.scrollTo({
          left: Math.max(0, Math.min(left, maxLeft)),
          behavior,
        });
      }

      pauseOtherVideos(activeVideo) {
        this.videos.forEach((video) => {
          if (video !== activeVideo && !video.paused) {
            video.pause();
          }
        });
      }

      isMobileLayout() {
        return window.matchMedia("(max-width: 749px)").matches;
      }
    }
  );
}
